import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowSquareOut,
  ArrowsClockwise,
  CaretRight,
  Funnel,
  Gear,
  Plus,
  UsersThree,
} from '@phosphor-icons/react';

import {
  listSignupSummary,
  type SignupSummaryRow,
  type SignupStatus,
} from '@/api/signups';
import { listTeams, type Team } from '@/api/teams';
import { listMyOrganizations } from '@/api/orgs';
import { publicURL as websitePublicURL } from '@/api/websites';
import { SignupQueueTable } from '@/components/SignupQueueTable';
import { LoadError } from '@/components/LoadError';
import {
  SignupFilterPanel,
  EMPTY_SIGNUP_FILTERS,
  countActiveSignupFilters,
  type SignupFilters,
} from '@/components/SignupFilterPanel';
import { TeamSheet } from '@/components/TeamSheet';
import { TeamModal } from '@/components/TeamModal';
import { EmptyState } from '@/components/ui/empty-state';
import {
  RowActions,
  StickyEndCell,
  useHorizontalScrollEdge,
  type RowAction,
} from '@/components/ui/row-actions';
import { TablePagination } from '@/components/ui/table-pagination';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import {
  SortableHeaderButton,
  useTableSortState,
} from '@/components/ui/table-sort';
import { config } from '@/config';
import { resolve as resolveTeamColor } from '@/lib/teamColor';
import { cn } from '@/lib/utils';

// Signups page — cross-team Anmälningar overview.
//
// One row per team showing the signup_* config snapshot + per-status counts.
// Expanding a row drops the applications queue inline (SignupQueueTable),
// mirroring the Teams page's accordion pattern.
//
// Per-team signup *settings* live on the TeamSheet's "Signup settings" tab,
// not here — the row's 3-dot menu deep-links into that tab so admins can
// configure capacity / fee / required fields without leaving the page.

const CHEVRON_COL = '2rem';
const ACTIONS_COL = '4rem';

// Fixed column widths — the table is wider than max-w-6xl on purpose so the
// outer overflow-x-auto kicks in horizontal scrolling. Widening "Platser"
// keeps the "Obegränsat" badge intact instead of truncating.
function gridTemplate(): string {
  return [
    CHEVRON_COL,
    '16rem', // team name
    '7rem', //  members (current roster) — left of status
    '8rem', //  status badge
    '8rem', //  mode
    '7rem', //  submitted
    '7rem', //  waitlisted
    '10rem', // capacity
    ACTIONS_COL,
  ].join(' ');
}

type SortColumn =
  | 'name'
  | 'members'
  | 'enabled'
  | 'mode'
  | 'submitted'
  | 'waitlisted'
  | 'capacity';

// applySignupFilters narrows the summary rows by the panel's selections.
// AND across filter groups, OR within a group — same semantics as the
// Members filter panel so users carry the mental model across pages.
function applySignupFilters(
  rows: SignupSummaryRow[],
  f: SignupFilters,
): SignupSummaryRow[] {
  if (
    f.teamIds.length === 0 &&
    f.categoryIds.length === 0 &&
    f.enabledStates.length === 0 &&
    f.modes.length === 0 &&
    f.activity.length === 0
  ) {
    return rows;
  }
  return rows.filter((r) => {
    if (f.teamIds.length > 0 && !f.teamIds.includes(r.team_id)) return false;
    if (f.categoryIds.length > 0) {
      if (!r.category_id || !f.categoryIds.includes(r.category_id)) return false;
    }
    if (f.enabledStates.length > 0) {
      const state = r.enabled ? 'open' : 'closed';
      if (!f.enabledStates.includes(state)) return false;
    }
    if (f.modes.length > 0 && !f.modes.includes(r.mode)) return false;
    if (f.activity.length > 0) {
      const hasSpots = r.capacity === null || r.confirmed_count < r.capacity;
      const matches = f.activity.every((key) => {
        if (key === 'has_submitted') return r.submitted_count > 0;
        if (key === 'has_waitlist') return r.waitlisted_count > 0;
        if (key === 'has_capacity') return hasSpots;
        return true;
      });
      if (!matches) return false;
    }
    return true;
  });
}

// Display status shown in the Status column. Derived from the config toggle
// AND the scheduled window — a team can be enabled but not yet open (future
// opens_at) or past its close (elapsed closes_at). Keying the badge off
// `enabled` alone made a scheduled team read as "Öppen" before it actually
// accepted anyone.
export type SignupDisplayState = 'open' | 'scheduled' | 'closed';

export function deriveSignupState(
  row: SignupSummaryRow,
  now: number = Date.now(),
): SignupDisplayState {
  if (!row.enabled) return 'closed';
  if (row.opens_at) {
    const opens = Date.parse(row.opens_at);
    if (!Number.isNaN(opens) && opens > now) return 'scheduled';
  }
  if (row.closes_at) {
    const closes = Date.parse(row.closes_at);
    if (!Number.isNaN(closes) && closes <= now) return 'closed';
  }
  return 'open';
}

// 24h date+time for the "opens {{date}}" tooltip — locale-aware text, never
// AM/PM (SportAdmin is 24h everywhere).
function formatOpensAt(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale || undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    hour12: false,
  }).format(d);
}

export function Signups() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Per-team status filter state. Held at the page level so toggling a
  // pill inside one team's accordion doesn't affect a different team's
  // queue that the user already drilled into.
  const [statusFilters, setStatusFilters] = useState<
    Record<string, SignupStatus | ''>
  >({});
  // Team-sheet target — opens TeamSheet on the Signup-settings tab.
  const [sheetTeam, setSheetTeam] = useState<Team | null>(null);
  // New-team modal (folded in from the former standalone Teams page).
  const [addOpen, setAddOpen] = useState(false);
  // Client-side paging — N teams per org is bounded, so the summary
  // endpoint returns every row in one call and we slice locally.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Filter panel state — the panel itself owns the form; we own the values
  // and apply them to the rows before paging.
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<SignupFilters>(EMPTY_SIGNUP_FILTERS);
  const activeFilterCount = countActiveSignupFilters(filters);

  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const org = orgsQuery.data?.find((o) => o.slug === slug);
  const callerRole = org?.role ?? '';
  const canManage = callerRole === 'owner' || callerRole === 'administrator';

  const summaryQuery = useQuery({
    queryKey: ['signup-summary', slug],
    queryFn: () => listSignupSummary(token, slug),
    enabled: !!token && !!slug,
  });

  // Teams list — needed to hand a full Team object to TeamSheet when the
  // user opens settings from a summary row. The summary endpoint only
  // returns the columns the overview cares about, not the full team row.
  const teamsQuery = useQuery({
    queryKey: ['teams', slug],
    queryFn: () => listTeams(token, slug),
    enabled: !!token && !!slug,
  });
  const teamsByID = useMemo(() => {
    const map = new Map<string, Team>();
    for (const team of teamsQuery.data?.teams ?? []) map.set(team.id, team);
    return map;
  }, [teamsQuery.data]);

  // Hooks must run on every render — keep this above the early returns below,
  // otherwise the loading→loaded transition changes the hook count and React
  // throws "Rendered more hooks than during the previous render".
  const allRows = useMemo(
    () => summaryQuery.data?.teams ?? [],
    [summaryQuery.data],
  );
  const rows = useMemo(
    () => applySignupFilters(allRows, filters),
    [allRows, filters],
  );

  if (orgsQuery.isPending) {
    return (
      <p className="p-6 text-sm text-muted-foreground">{t('common.loading')}</p>
    );
  }
  if (!org) {
    return (
      <p className="p-6 text-sm text-destructive">{t('signups.notFound')}</p>
    );
  }

  // Refresh re-fetches both the per-team summary counts and every expanded
  // queue currently open below the rows. Cheap enough to fan out — the
  // signups query keys are all prefixed with the slug so this stays scoped.
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['signup-summary', slug] });
    void queryClient.invalidateQueries({ queryKey: ['signups', slug] });
  };
  const refreshing =
    summaryQuery.isFetching ||
    queryClient.isFetching({ queryKey: ['signups', slug] }) > 0;

  // Prefer the new signup portal (`<portalBaseURL>/o/<slug>`) so admins land
  // on the same page their members will see. Falls back to the legacy
  // `/c/<slug>/signup` form when the portal URL isn't configured (dev runs
  // without local-bootstrap's portal patches).
  const publicSignupURL = config.portalBaseURL
    ? `${config.portalBaseURL}/o/${encodeURIComponent(slug)}`
    : `${websitePublicURL(config.apiURL, config.publicSitesBaseDomain, slug)}/signup`;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 p-4 md:p-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <p className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('signups.heading')}
          </p>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight md:text-4xl">
            {t('signups.summary.title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('signups.summary.intro')}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 self-start sm:self-auto">
          <button
            type="button"
            onClick={() => setFilterOpen(true)}
            aria-label={t('signups.filter.button')}
            className="relative inline-flex h-10 items-center gap-2 rounded-pill border border-border bg-background px-4 text-sm font-semibold text-foreground/80 transition-colors hover:border-foreground/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Funnel className="h-4 w-4" weight="bold" aria-hidden />
            {t('signups.filter.button')}
            {activeFilterCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-pill bg-primary px-1.5 font-heading text-[0.65rem] font-bold text-primary-foreground">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            aria-label={t('signups.summary.refresh')}
            className="inline-flex h-10 items-center gap-2 rounded-pill border border-border bg-background px-4 text-sm font-semibold text-foreground/80 transition-colors hover:border-foreground/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-wait disabled:opacity-60"
          >
            <ArrowsClockwise
              className={cn('h-4 w-4', refreshing && 'animate-spin')}
              weight="bold"
              aria-hidden
            />
            {t('signups.summary.refresh')}
          </button>
          <a
            href={publicSignupURL}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex h-10 items-center gap-2 rounded-pill border border-border bg-background px-4 text-sm font-semibold text-foreground/80 transition-colors hover:border-foreground/30 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('signups.preview.viewPublicPage')}
            <ArrowSquareOut className="h-4 w-4" weight="bold" aria-hidden />
          </a>
          {canManage && (
            <button
              type="button"
              onClick={() => setAddOpen(true)}
              className="inline-flex h-10 items-center gap-2 rounded-pill bg-primary px-4 text-sm font-semibold text-primary-foreground transition-transform hover:scale-[1.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plus className="h-4 w-4" weight="bold" aria-hidden />
              {t('teams.addCta')}
            </button>
          )}
        </div>
      </header>

      {summaryQuery.isLoading && <TableSkeleton columns={5} />}

      {summaryQuery.isError && (
        <LoadError onRetry={() => void summaryQuery.refetch()} />
      )}

      {summaryQuery.data && rows.length === 0 && allRows.length === 0 && (
        <EmptyState
          icon={<UsersThree weight="duotone" />}
          title={t('signups.summary.empty.title')}
          body={t('signups.summary.empty.body')}
          actions={
            canManage
              ? [
                  {
                    label: t('signups.summary.empty.cta'),
                    to: `/orgs/${slug}/teams`,
                    icon: <UsersThree weight="bold" />,
                  },
                ]
              : undefined
          }
        />
      )}

      {summaryQuery.data && rows.length === 0 && allRows.length > 0 && (
        <EmptyState
          icon={<Funnel weight="duotone" />}
          title={t('signups.summary.noMatches.title')}
          body={t('signups.summary.noMatches.body')}
          actions={[
            {
              label: t('signups.filter.clearAll'),
              onClick: () => setFilters(EMPTY_SIGNUP_FILTERS),
              icon: <Funnel weight="bold" />,
            },
          ]}
        />
      )}

      {rows.length > 0 && (
        <>
          <SummaryList
            rows={rows}
            slug={slug}
            canManage={canManage}
            expanded={expanded}
            onToggle={(id) =>
              setExpanded((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            statusFilters={statusFilters}
            onStatusFilterChange={(teamID, status) =>
              setStatusFilters((prev) => ({ ...prev, [teamID]: status }))
            }
            onOpenSettings={(teamID) => {
              const team = teamsByID.get(teamID);
              if (team) setSheetTeam(team);
            }}
            page={page}
            pageSize={pageSize}
          />
          <TablePagination
            total={rows.length}
            page={page}
            size={pageSize}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />
        </>
      )}

      <SignupFilterPanel
        slug={slug}
        open={filterOpen}
        onOpenChange={setFilterOpen}
        filters={filters}
        onChange={(next) => {
          setFilters(next);
          setPage(1);
        }}
      />

      <TeamSheet
        open={sheetTeam !== null}
        onOpenChange={(open) => !open && setSheetTeam(null)}
        slug={slug}
        team={sheetTeam}
        initialTab="signups"
      />

      <TeamModal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          // TeamModal invalidates ['teams'] but not the signup summary this
          // page reads — refresh it so a newly created team appears.
          void queryClient.invalidateQueries({
            queryKey: ['signup-summary', slug],
          });
        }}
        slug={slug}
      />
    </div>
  );
}

function SummaryList({
  rows,
  slug,
  canManage,
  expanded,
  onToggle,
  statusFilters,
  onStatusFilterChange,
  onOpenSettings,
  page,
  pageSize,
}: {
  rows: SignupSummaryRow[];
  slug: string;
  canManage: boolean;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  statusFilters: Record<string, SignupStatus | ''>;
  onStatusFilterChange: (teamID: string, status: SignupStatus | '') => void;
  onOpenSettings: (teamID: string) => void;
  page: number;
  pageSize: number;
}) {
  const { t } = useTranslation('common');
  const scrollRef = useRef<HTMLDivElement>(null);
  const elevated = useHorizontalScrollEdge(scrollRef);
  const template = gridTemplate();

  const sortState = useTableSortState<SortColumn>({
    columns: [
      'name',
      'members',
      'enabled',
      'mode',
      'submitted',
      'waitlisted',
      'capacity',
    ] as const,
  });

  // Rows are grouped by team category (when any team has one) with a section
  // divider per category; the active column sort applies WITHIN each group, so
  // category is always the primary ordering and clicking a header re-sorts the
  // teams inside each category. Uncategorized teams sort into a trailing
  // "no category" group. When no team has a category we render flat.
  const { rows: ordered, hasCategories } = useMemo(() => {
    const hasCategories = rows.some((r) => r.category_id);
    const factor = sortState.dir === 'asc' ? 1 : -1;
    const within = (a: SignupSummaryRow, b: SignupSummaryRow): number => {
      switch (sortState.sort) {
        case 'name':
          return a.team_name.localeCompare(b.team_name) * factor;
        case 'enabled':
          return (Number(a.enabled) - Number(b.enabled)) * factor;
        case 'mode':
          return a.mode.localeCompare(b.mode) * factor;
        case 'submitted':
          return (a.submitted_count - b.submitted_count) * factor;
        case 'members':
          return (a.member_count - b.member_count) * factor;
        case 'waitlisted':
          return (a.waitlisted_count - b.waitlisted_count) * factor;
        case 'capacity':
          return ((a.capacity ?? 0) - (b.capacity ?? 0)) * factor;
        default:
          return a.team_name.localeCompare(b.team_name);
      }
    };
    const copy = [...rows];
    copy.sort((a, b) => {
      if (hasCategories) {
        // Uncategorized teams sort last; otherwise alphabetical by category.
        const ca = a.category_name ?? '';
        const cb = b.category_name ?? '';
        if (!ca && cb) return 1;
        if (ca && !cb) return -1;
        const byCat = ca.localeCompare(cb);
        if (byCat !== 0) return byCat;
      }
      return within(a, b);
    });
    return { rows: copy, hasCategories };
  }, [rows, sortState.sort, sortState.dir]);

  const visible = useMemo(() => {
    const start = (page - 1) * pageSize;
    return ordered.slice(start, start + pageSize);
  }, [ordered, page, pageSize]);

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-border bg-background shadow-sm"
      style={{ clipPath: 'inset(0 round var(--radius-2xl))' }}
    >
      <div ref={scrollRef} className="overflow-x-auto">
        <div className="min-w-fit divide-y divide-border">
          <div
            className="hidden bg-muted/40 py-3 sm:grid"
            style={{ gridTemplateColumns: template }}
            role="row"
          >
            <span aria-hidden />
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="name"
                label={t('signups.summary.col.team')}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="members"
                label={t('signups.summary.col.members')}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="enabled"
                label={t('signups.summary.col.status')}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="mode"
                label={t('signups.summary.col.mode')}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="submitted"
                label={t('signups.summary.col.submitted')}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="waitlisted"
                label={t('signups.summary.col.waitlisted')}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <span className="px-2" role="columnheader">
              <SortableHeaderButton
                column="capacity"
                label={t('signups.summary.col.capacity')}
                state={sortState}
                className="font-heading text-[0.75rem] font-semibold uppercase tracking-wider"
              />
            </span>
            <StickyEndCell elevated={false} bgClass="bg-muted/40" className="justify-end">
              <span className="sr-only">{t('rowActions.columnHeader')}</span>
            </StickyEndCell>
          </div>

          {(() => {
            const out: ReactNode[] = [];
            let prevCat: string | undefined;
            for (const row of visible) {
              const catKey = row.category_id ?? '';
              if (hasCategories && catKey !== prevCat) {
                out.push(
                  <CategoryHeader
                    key={`cat-${catKey || 'none'}`}
                    name={row.category_name}
                  />,
                );
                prevCat = catKey;
              }
              out.push(
                <SummaryRow
                  key={row.team_id}
                  row={row}
                  template={template}
                  elevated={elevated}
                  expanded={expanded.has(row.team_id)}
                  onToggle={() => onToggle(row.team_id)}
                  statusFilter={statusFilters[row.team_id] ?? ''}
                  onStatusFilterChange={(s) =>
                    onStatusFilterChange(row.team_id, s)
                  }
                  onOpenSettings={() => onOpenSettings(row.team_id)}
                  slug={slug}
                  canManage={canManage}
                />,
              );
            }
            return out;
          })()}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({
  row,
  template,
  elevated,
  expanded,
  onToggle,
  statusFilter,
  onStatusFilterChange,
  onOpenSettings,
  slug,
  canManage,
}: {
  row: SignupSummaryRow;
  template: string;
  elevated: boolean;
  expanded: boolean;
  onToggle: () => void;
  statusFilter: SignupStatus | '';
  onStatusFilterChange: (s: SignupStatus | '') => void;
  onOpenSettings: () => void;
  slug: string;
  canManage: boolean;
}) {
  const { t } = useTranslation('common');
  const palette = resolveTeamColor(row.team_name, row.team_color || null);

  const menuActions: RowAction[] = canManage
    ? [
        {
          id: 'settings',
          label: t('common.edit'),
          icon: Gear,
          onSelect: onOpenSettings,
        },
      ]
    : [];

  return (
    <div className="divide-y divide-border">
      <div
        className="group grid items-stretch transition-colors hover:bg-muted/40"
        style={{ gridTemplateColumns: template }}
        role="row"
      >
        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={onToggle}
            aria-label={
              expanded
                ? t('signups.summary.collapseRow')
                : t('signups.summary.expandRow')
            }
            aria-expanded={expanded}
            className="flex h-8 w-8 items-center justify-center rounded-pill text-foreground/70 transition-colors hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CaretRight
              className={cn(
                'h-4 w-4 transition-transform duration-150',
                expanded && 'rotate-90',
              )}
            />
          </button>
        </div>

        <span
          className="flex items-center gap-2 px-2 py-3 text-sm"
          role="cell"
        >
          <span
            aria-hidden
            title={palette.hex}
            className="h-3 w-3 shrink-0 rounded-pill"
            style={{
              background: palette.dot,
              border: `1px solid ${palette.border}`,
            }}
          />
          <span className="truncate font-heading font-semibold text-foreground">
            {row.team_name}
          </span>
        </span>

        <span
          className="flex items-center px-2 py-3 text-sm tabular-nums"
          role="cell"
        >
          <CountChip count={row.member_count} />
        </span>

        <span
          className="flex items-center justify-center px-2 py-3 text-sm"
          role="cell"
        >
          <StatusBadge row={row} />
        </span>

        <span
          className="flex items-center px-2 py-3 text-sm"
          role="cell"
        >
          <ModeBadge mode={row.mode} />
        </span>

        <span
          className="flex items-center px-2 py-3 text-sm tabular-nums"
          role="cell"
        >
          <CountChip count={row.submitted_count} />
        </span>

        <span
          className="flex items-center px-2 py-3 text-sm tabular-nums"
          role="cell"
        >
          <CountChip count={row.waitlisted_count} />
        </span>

        <span
          className="flex items-center px-2 py-3 text-sm tabular-nums text-foreground/80"
          role="cell"
        >
          <CapacityCell row={row} />
        </span>

        <StickyEndCell
          elevated={elevated}
          bgClass="bg-background group-hover:bg-muted"
        >
          {menuActions.length > 0 && <RowActions menuActions={menuActions} />}
        </StickyEndCell>
      </div>

      {expanded && (
        <div className="border-t border-border/60 bg-muted/15 p-4">
          <SignupQueueTable
            slug={slug}
            teamID={row.team_id}
            canManage={canManage}
            // canManage on this page IS the org-admin check
            // (owner || administrator), so it doubles as isOrgAdmin — the
            // self-decision exemption. See SignupQueueTable prop docs.
            isOrgAdmin={canManage}
            statusFilter={statusFilter}
            onStatusFilterChange={onStatusFilterChange}
          />
        </div>
      )}
    </div>
  );
}

// CapacityCell renders the "Platser" column. When the team has no
// capacity cap we show a neutral "Obegränsat" pill — matching the badge
// shape used elsewhere in this row — so the column stays scannable
// instead of mixing a badge (Status) with bare text. With a cap, we
// render the numeric "confirmed / capacity (+waitlist)" form unchanged.
function CapacityCell({ row }: { row: SignupSummaryRow }) {
  const { t } = useTranslation('common');
  if (row.capacity === null) {
    return (
      <span className="inline-flex items-center rounded-pill border border-border bg-muted px-2 py-0.5 text-[0.7rem] font-medium text-foreground/70">
        {t('signups.summary.capacity.unlimited')}
      </span>
    );
  }
  const label = row.waitlist_capacity
    ? `${row.confirmed_count}/${row.capacity} (+${row.waitlist_capacity})`
    : `${row.confirmed_count}/${row.capacity}`;
  return <span>{label}</span>;
}

function StatusBadge({ row }: { row: SignupSummaryRow }) {
  const { t, i18n } = useTranslation('common');
  const state = deriveSignupState(row);
  const tone =
    state === 'open'
      ? 'bg-primary text-primary-foreground ring-primary/60'
      : state === 'scheduled'
        ? 'bg-amber-100 text-amber-900 ring-amber-300/70 dark:bg-amber-500/15 dark:text-amber-100 dark:ring-amber-500/40'
        : 'bg-muted text-muted-foreground ring-border';
  const title =
    state === 'scheduled' && row.opens_at
      ? t('signups.summary.status.scheduledTitle', {
          date: formatOpensAt(row.opens_at, i18n.language),
        })
      : undefined;
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center justify-center rounded-pill px-2.5 py-0.5 text-xs font-semibold ring-1',
        tone,
      )}
    >
      {t(`signups.summary.status.${state}`)}
    </span>
  );
}

// ModeBadge tints each signup-mode pill so admins can scan a long table at
// a glance: emerald = auto-confirm, amber = needs review, purple = waitlist
// only. Disabled rows desaturate to muted so the eye lands on open teams.
function ModeBadge({
  mode,
}: {
  mode: 'open' | 'admin_approval' | 'waitlist_only';
}) {
  const { t } = useTranslation('common');
  const tone =
    mode === 'open'
      ? 'bg-emerald-100 text-emerald-900 ring-emerald-300/70 dark:bg-emerald-500/15 dark:text-emerald-100 dark:ring-emerald-500/40'
      : mode === 'admin_approval'
        ? 'bg-amber-100 text-amber-900 ring-amber-300/70 dark:bg-amber-500/15 dark:text-amber-100 dark:ring-amber-500/40'
        : 'bg-purple-100 text-purple-900 ring-purple-300/70 dark:bg-purple-500/15 dark:text-purple-100 dark:ring-purple-500/40';
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-pill px-2.5 py-0.5 text-xs font-semibold ring-1',
        tone,
      )}
    >
      {t(`signups.summary.mode.${mode}`, { defaultValue: mode })}
    </span>
  );
}

// CountChip renders a per-status count. Every count is a plain number for a
// consistent column — zero muted, non-zero bold foreground — so the eye lands
// on the teams with activity. (Previously non-zero rendered as a filled pill
// while zeros were bare text, which made the column read as uneven badges.)
function CountChip({ count }: { count: number }) {
  return (
    <span
      className={
        count === 0 ? 'text-foreground/40' : 'font-semibold text-foreground'
      }
    >
      {count}
    </span>
  );
}

// CategoryHeader is the section divider shown above each group of teams that
// share a team category, plus a trailing group for teams with none. Rendered
// as a quiet full-width eyebrow so it reads as structure rather than data.
function CategoryHeader({ name }: { name: string | undefined }) {
  const { t } = useTranslation('common');
  return (
    <div role="row" className="bg-muted/30 px-4 py-2">
      <span className="font-heading text-[0.7rem] font-semibold uppercase tracking-wider text-foreground/55">
        {name?.trim() || t('signups.summary.uncategorized')}
      </span>
    </div>
  );
}

